import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Trophy, Heart, Dog, Briefcase, Star } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_ICON = {
  ngo: Heart,
  breeder: Dog,
  business: Briefcase,
  sponsor: Star,
};

const TYPE_COLOR = {
  ngo: 'text-rose-600',
  breeder: 'text-amber-600',
  business: 'text-blue-600',
  sponsor: 'text-purple-600',
};

/**
 * Public "Top Contributing Partners" widget. Shows partners whose
 * promo codes have been redeemed at least once, ranked by redemption
 * count. Self-hides if the leaderboard is empty (no redemptions yet)
 * so we don't show a sad "0 partners contributing" placeholder on a
 * brand-new community.
 */
const PartnerLeaderboard = ({ limit = 5, className = '' }) => {
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/partner-codes/leaderboard`, { params: { limit } })
      .then((res) => setRows(Array.isArray(res.data) ? res.data : []))
      .catch(() => setRows([]))
      .finally(() => setLoaded(true));
  }, [limit]);

  if (!loaded || rows.length === 0) return null;

  return (
    <Card
      className={`border-amber-200 bg-amber-50/60 ${className}`}
      data-testid="partner-leaderboard"
    >
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <Trophy className="w-5 h-5 text-amber-600" />
          <h3 className="font-heading font-semibold">
            Top Contributing Partners
          </h3>
          <span className="text-xs text-muted-foreground ml-auto">
            By promo-code redemptions
          </span>
        </div>
        <ol className="space-y-2">
          {rows.map((row, idx) => {
            const Icon = TYPE_ICON[row.partner_type] || Briefcase;
            const color = TYPE_COLOR[row.partner_type] || 'text-amber-600';
            return (
              <li
                key={row.id}
                className="flex items-center gap-3 text-sm"
                data-testid={`leaderboard-row-${idx}`}
              >
                <span className="w-6 text-center font-mono font-bold text-amber-700">
                  {idx + 1}
                </span>
                <Icon className={`w-4 h-4 ${color} shrink-0`} />
                <span className="flex-1 truncate font-medium">
                  {row.partner_name || 'Unknown partner'}
                </span>
                <span className="text-xs text-muted-foreground tabular-nums">
                  {row.redemption_count}{' '}
                  {row.redemption_count === 1 ? 'member' : 'members'}
                </span>
              </li>
            );
          })}
        </ol>
        <p className="text-xs text-muted-foreground mt-3">
          Each redemption = a new member who joined the Club through one of these
          partners. Want your organization listed? Apply above.
        </p>
      </CardContent>
    </Card>
  );
};

export default PartnerLeaderboard;
